import { useCallback, useEffect, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ApiError } from '../../api/client';
import { getProfile } from '../../api/profile';
import { ProfileActionList } from '../../components/business/ProfileActionList';
import { ProfileHeader } from '../../components/business/ProfileHeader';
import { ProfileStatsGrid } from '../../components/business/ProfileStatsGrid';
import { AppText } from '../../components/ui/AppText';
import { EmptyView } from '../../components/ui/EmptyView';
import { ErrorView } from '../../components/ui/ErrorView';
import { LoadingView } from '../../components/ui/LoadingView';
import { showAppToast } from '../../components/ui/AppToast';
import { tokens } from '../../theme/tokens';
import type { ProfileActionViewModel, ProfileViewModel } from '../../types/profile';

type ProfilePageState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'empty' }
  | { status: 'success'; profile: ProfileViewModel };

const isProfileEmpty = (profile: ProfileViewModel): boolean => profile.actions.length === 0;

const getErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.code === 'unauthorized') {
      return '登录状态已失效，请重新登录';
    }
    return error.message;
  }
  return '暂时无法加载我的页面';
};

export default function ProfileTabPage() {
  const router = useRouter();
  const [state, setState] = useState<ProfilePageState>({ status: 'loading' });

  const loadProfile = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const profile = await getProfile();
      if (isProfileEmpty(profile)) {
        setState({ status: 'empty' });
        return;
      }
      setState({ status: 'success', profile });
    } catch (error: unknown) {
      setState({ status: 'error', message: getErrorMessage(error) });
    }
  }, []);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  const showDevelopmentToast = useCallback((featureName: string) => {
    showAppToast(`${featureName}功能开发中`, 'info');
  }, []);

  const openAction = useCallback(
    (action: ProfileActionViewModel) => {
      if (action.routeKey === 'wrong-questions') {
        router.push('/wrong-questions');
        return;
      }
      if (action.routeKey === 'study-plan') {
        router.push('/study-plan');
        return;
      }
      if (action.routeKey === 'theme-settings') {
        router.push('/theme-settings');
        return;
      }
      if (action.routeKey === 'settings') {
        router.push('/settings');
        return;
      }
      if (action.routeKey === 'learning-records') {
        router.push('/learning-records');
        return;
      }
      if (action.routeKey === 'ranking') {
        router.push('/ranking');
        return;
      }
      if (action.routeKey === 'flashcards') {
        router.push('/flashcards');
        return;
      }
      showDevelopmentToast(action.title);
    },
    [router, showDevelopmentToast]
  );

  if (state.status === 'loading') {
    return (
      <SafeAreaView style={styles.screen}>
        <LoadingView message="正在加载我的页面" />
      </SafeAreaView>
    );
  }

  if (state.status === 'error') {
    return (
      <SafeAreaView style={styles.screen}>
        <ErrorView message={state.message} onRetry={loadProfile} />
      </SafeAreaView>
    );
  }

  if (state.status === 'empty') {
    return (
      <SafeAreaView style={styles.screen}>
        <EmptyView
          title="暂无个人信息"
          description="登录后会展示学习资料、偏好设置和账号入口。"
          actionLabel="重新加载"
          onAction={loadProfile}
        />
      </SafeAreaView>
    );
  }

  const { profile } = state;

  return (
    <SafeAreaView style={styles.screen}>
      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.header}>
          <AppText variant="title" weight="bold">
            我的
          </AppText>
          <AppText variant="body" tone="muted">
            管理学习资料、账号与偏好设置。
          </AppText>
        </View>

        <ProfileHeader
          displayName={profile.displayName}
          gradeName={profile.gradeName}
          avatarLabel={profile.avatarLabel}
          streakDays={profile.streakDays}
          completedTaskCount={profile.completedTaskCount}
        />

        <ProfileStatsGrid
          learningHours={profile.learningHours}
          masteredKnowledgeCount={profile.masteredKnowledgeCount}
          streakDays={profile.streakDays}
        />

        <View style={styles.sectionHeader}>
          <AppText variant="subtitle" weight="bold">
            设置与服务
          </AppText>
        </View>

        <ProfileActionList actions={profile.actions} onPressAction={openAction} />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: tokens.colors.background,
  },
  content: {
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.lg,
    paddingBottom: tokens.spacing.xxl,
    gap: tokens.spacing.lg,
  },
  header: {
    gap: tokens.spacing.sm,
  },
  sectionHeader: {
    marginTop: tokens.spacing.sm,
  },
});
